import { ThemeEvents } from '@theme/events';

const VISIBLE_PROPERTY_NAME = 'properties[Included Pin]';
const PIN_VARIANT_PROPERTY_NAME = 'properties[_pin_variant_id]';
const PIN_SKU_PROPERTY_NAME = 'properties[_pin_sku]';

class PinOptions extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {MutationObserver | null} */
  #formObserver = null;
  /** @type {WeakMap<HTMLElement, Animation>} */
  #disclosureAnimations = new WeakMap();
  /** @type {any} */
  #data = null;
  /** @type {any | null} */
  #selectedOption = null;
  /** @type {{profileIndex: number, length: string} | null} */
  #preferredSelection = null;
  /** @type {boolean} */
  #hasUserSelectedPin = false;

  connectedCallback() {
    this.#data = this.#readData();
    if (!this.#data) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const eventTarget = this.closest('.shopify-section, dialog, product-card') || this;

    eventTarget.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    eventTarget.addEventListener('change', this.#handleScopedChange, { signal });
    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('change', this.#handleOptionChange, { signal });

    this.#observeFormAvailability();
    this.#resolveForVariant(this.#getCurrentVariantId(), false);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#formObserver?.disconnect();
    this.#removeFormInputs();
  }

  #readData() {
    const script = this.querySelector('script[data-pin-options-data]');
    if (!(script instanceof HTMLScriptElement)) return null;

    try {
      return JSON.parse(script.textContent || '{}');
    } catch (error) {
      console.error('Unable to parse included pin options data', error);
      return null;
    }
  }

  #handleVariantUpdate = (event) => {
    const productId = event.detail?.data?.productId;
    if (productId && String(productId) !== String(this.dataset.productId)) return;

    this.#resolveForVariant(event.detail?.resource?.id || '', true);
  };

  #handleScopedChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.name !== 'id') return;

    const productForm = this.#findProductFormComponent();
    if (!productForm?.contains(target)) return;

    this.#resolveForVariant(target.value, true);
  };

  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const radio = target.closest(`label`)?.querySelector(`input[name="${this.#radioName}"]`);
    if (radio instanceof HTMLInputElement) {
      let option = null;
      for (const eligibleOption of this.#eligibleOptions) {
        if (String(eligibleOption.variantId) === radio.value) {
          option = eligibleOption;
          break;
        }
      }

      if (option) {
        this.#hasUserSelectedPin = true;
        this.#preferredSelection = { profileIndex: option.profileIndex, length: option.length };
      }
    }

    if (!target.closest('[data-pin-options-toggle]')) return;

    this.#setExpanded(!this.#isExpanded());
  };

  #handleOptionChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.name !== this.#radioName) return;

    const option = this.#eligibleOptions.find((eligibleOption) => String(eligibleOption.variantId) === target.value);
    if (!option) return;

    this.#hasUserSelectedPin = true;
    this.#applySelection(option, { userSelected: true });
    this.#setExpanded(false);
  };

  #observeFormAvailability() {
    const root = this.#scopedRoot;
    if (!root) return;

    this.#formObserver = new MutationObserver(() => {
      this.#syncFormInputs(this.#selectedOption);
    });
    this.#formObserver.observe(root, { childList: true, subtree: true });
  }

  #resolveForVariant(variantId, preserveCurrentSelection) {
    this.dataset.selectedVariantId = String(variantId || '');
    const eligibleOptions = this.#dedupeOptions(this.#eligibleOptions);

    if (eligibleOptions.length === 0) {
      this.#hideSelector();
      return;
    }

    const nextOption = this.#chooseOption(eligibleOptions, preserveCurrentSelection);
    this.hidden = false;
    this.#renderOptions(eligibleOptions, nextOption);
    this.#applySelection(nextOption);
  }

  #chooseOption(eligibleOptions, preserveCurrentSelection) {
    const previousSelection = preserveCurrentSelection && this.#hasUserSelectedPin ? this.#preferredSelection : null;

    if (previousSelection) {
      const sameProfileAndLength = eligibleOptions.find(
        (option) => option.profileIndex === previousSelection.profileIndex && option.length === previousSelection.length
      );
      if (sameProfileAndLength) return sameProfileAndLength;

      const sameProfileDefault = eligibleOptions.find(
        (option) => option.profileIndex === previousSelection.profileIndex && option.isDefaultLength
      );
      if (sameProfileDefault) return sameProfileDefault;
    }

    const firstProfileIndex = this.#data?.profiles?.[0]?.index;
    const firstProfileDefault = eligibleOptions.find(
      (option) => option.profileIndex === firstProfileIndex && option.isDefaultLength
    );

    return firstProfileDefault || eligibleOptions[0];
  }

  #renderOptions(eligibleOptions, selectedOption) {
    const list = this.#listElement;
    if (!list) return;

    const fragment = document.createDocumentFragment();

    for (const option of eligibleOptions) {
      const label = document.createElement('label');
      label.className = 'pin-options__option';

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = this.#radioName;
      input.value = String(option.variantId);
      input.id = `${this.#instanceId}-option-${option.variantId}`;
      input.checked = String(option.variantId) === String(selectedOption.variantId);

      const text = document.createElement('span');
      text.className = 'pin-options__option-text';
      text.textContent = this.#formatOption(option);

      label.append(input, text);

      if (option.isDefaultLength && this.#badgeLabel) {
        const badge = document.createElement('span');
        badge.className = 'pin-options__badge';
        badge.textContent = this.#badgeLabel;
        label.append(badge);
      }

      fragment.append(label);
    }

    list.replaceChildren(fragment);
  }

  #dedupeOptions(options) {
    const seenOptions = new Set();

    return options.filter((option) => {
      const key = [option.profileIndex, option.profileLabel, option.length].join('::');
      if (seenOptions.has(key)) return false;

      seenOptions.add(key);
      return true;
    });
  }

  #applySelection(option, { userSelected = false } = {}) {
    this.#selectedOption = option;

    if (userSelected) {
      this.#preferredSelection = option ? { profileIndex: option.profileIndex, length: option.length } : null;
    }

    const summary = this.querySelector('[data-pin-options-summary]');
    if (summary) summary.textContent = option ? this.#formatOption(option) : '';

    this.#syncCheckedOption(option);
    this.#syncFormInputs(option);
  }

  #syncCheckedOption(option) {
    const inputs = this.querySelectorAll(`input[name="${this.#radioName}"]`);
    for (const input of inputs) {
      if (input instanceof HTMLInputElement) {
        input.checked = Boolean(option && String(input.value) === String(option.variantId));
      }
    }
  }

  #syncFormInputs(option) {
    const form = this.#shopifyForm;
    if (!form) return;

    if (!option) {
      this.#removeFormInputs(form);
      return;
    }

    this.#ensureInput(form, VISIBLE_PROPERTY_NAME, this.#formatOption(option), 'included-pin');
    this.#ensureInput(form, PIN_VARIANT_PROPERTY_NAME, String(option.variantId), 'pin-variant-id');
    this.#ensureInput(form, PIN_SKU_PROPERTY_NAME, option.sku || '', 'pin-sku');
  }

  #ensureInput(form, name, value, suffix) {
    let input = this.#ownedInputs(form).find((ownedInput) => ownedInput.name === name);

    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.id = `${this.#instanceId}-${suffix}`;
      input.dataset.pinOptionsOwner = this.#instanceId;
      form.append(input);
    }

    input.disabled = false;
    input.value = value;
  }

  #removeFormInputs(form = this.#shopifyForm) {
    if (!form) return;

    for (const input of this.#ownedInputs(form)) {
      input.remove();
    }
  }

  #ownedInputs(form) {
    return Array.from(form.querySelectorAll('input[data-pin-options-owner]')).filter(
      (input) => input instanceof HTMLInputElement && input.dataset.pinOptionsOwner === this.#instanceId
    );
  }

  #hideSelector() {
    this.hidden = true;
    this.#selectedOption = null;
    this.#setExpanded(false, { animate: false });
    this.#removeFormInputs();
  }

  #setExpanded(expanded, { animate = true } = {}) {
    const list = this.#listElement;
    const toggle = this.querySelector('[data-pin-options-toggle]');

    if (list) this.#setDisclosureState(list, expanded, { animate });
    if (toggle instanceof HTMLButtonElement) toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  /**
   * @param {HTMLElement} element
   * @param {boolean} isOpen
   * @param {{ animate?: boolean }} options
   */
  #setDisclosureState(element, isOpen, { animate = false } = {}) {
    if (!animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      element.hidden = !isOpen;
      return;
    }

    this.#disclosureAnimations.get(element)?.cancel();

    if (isOpen) {
      element.hidden = false;
    }

    const startHeight = isOpen ? 0 : element.getBoundingClientRect().height;
    const endHeight = isOpen ? element.scrollHeight : 0;

    element.style.overflow = 'hidden';

    const animation = element.animate(
      [
        { blockSize: `${startHeight}px`, opacity: isOpen ? 0 : 1 },
        { blockSize: `${endHeight}px`, opacity: isOpen ? 1 : 0 },
      ],
      {
        duration: 180,
        easing: 'ease-out',
      }
    );

    this.#disclosureAnimations.set(element, animation);

    animation.onfinish = () => {
      element.hidden = !isOpen;
      element.style.overflow = '';
      this.#disclosureAnimations.delete(element);
    };

    animation.oncancel = () => {
      element.style.overflow = '';
      this.#disclosureAnimations.delete(element);
    };
  }

  #isExpanded() {
    return this.#listElement?.hidden === false;
  }

  #formatOption(option) {
    return [option.profileLabel, option.length].filter(Boolean).join(' \u00b7 ');
  }

  get #badgeLabel() {
    return this.dataset.pinOptionsBadgeLabel || '(Most Popular)';
  }

  #getCurrentVariantId() {
    const input = this.#shopifyForm?.querySelector('input[name="id"]');
    if (input instanceof HTMLInputElement && input.value) return input.value;

    return this.dataset.selectedVariantId || this.#data?.selectedVariantId || '';
  }

  #findProductFormComponent() {
    const directForm = this.closest('product-form-component');
    if (directForm instanceof HTMLElement) return directForm;

    const roots = [
      this.closest('.product-details'),
      this.closest('quick-add-dialog'),
      this.closest('dialog'),
      this.closest('[data-testid="product-information"]'),
      this.closest('.shopify-section'),
      this.closest('product-card'),
    ].filter((root, index, rootsArray) => root instanceof Element && rootsArray.indexOf(root) === index);

    for (const root of roots) {
      const candidates = Array.from(root.querySelectorAll('product-form-component')).filter((candidate) => {
        if (!(candidate instanceof HTMLElement)) return false;
        return !this.dataset.productId || String(candidate.dataset.productId) === String(this.dataset.productId);
      });

      const followingCandidate = candidates.find((candidate) =>
        Boolean(this.compareDocumentPosition(candidate) & Node.DOCUMENT_POSITION_FOLLOWING)
      );

      if (followingCandidate) return followingCandidate;
      if (candidates[0] instanceof HTMLElement) return candidates[0];
    }

    return null;
  }

  get #eligibleOptions() {
    return this.#data?.variants?.[String(this.dataset.selectedVariantId || '')] || [];
  }

  get #shopifyForm() {
    const productForm = this.#findProductFormComponent();
    return productForm?.querySelector('form[data-type="add-to-cart-form"]') || null;
  }

  get #scopedRoot() {
    return (
      this.closest('.product-details') ||
      this.closest('quick-add-dialog') ||
      this.closest('dialog') ||
      this.closest('[data-testid="product-information"]') ||
      this.closest('.shopify-section') ||
      this.closest('product-card')
    );
  }

  get #listElement() {
    return this.querySelector('[data-pin-options-list]');
  }

  get #instanceId() {
    return this.dataset.instanceId || 'pin-options';
  }

  get #radioName() {
    return `${this.#instanceId}-pin-option`;
  }
}

if (!customElements.get('pin-options')) {
  customElements.define('pin-options', PinOptions);
}